import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  Timestamp,
  deleteDoc
} from "firebase/firestore";
import { db } from "../firebase";
import "./CookingRequest.css";

const CookingRequest = () => {
  const [cookingRequests, setCookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCookingRequests = async () => {
      try {
        setLoading(true);
        // Fetch only pending cooking requests
        const q = query(collection(db, "kitchen_requests"), where("status", "==", "pending"));
        const data = await getDocs(q);
        setCookingRequests(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
        setError(null);
      } catch (err) {
        setError("Failed to fetch cooking requests.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCookingRequests();
  }, []);

  const handleApproveRequest = async (requestId) => {
    setError(null);

    try {
      // Use a transaction to ensure data consistency
      await runTransaction(db, async (transaction) => {
        const requestDocRef = doc(db, 'kitchen_requests', requestId);
        const requestDoc = await transaction.get(requestDocRef);
        if (!requestDoc.exists()) throw new Error("Request not found!");
        
        const requestData = requestDoc.data();
        
        // Move the approved request to 'kitchen_approved' collection
        const newDocRef = doc(collection(db, 'kitchen_approved'));
        const approvedData = { 
          ...requestData, 
          status: 'approved', 
          approvedAt: Timestamp.now() 
        };
        transaction.set(newDocRef, approvedData);
        transaction.delete(requestDocRef); // Delete the original pending request
      });

      console.log("Cooking request approval successful!");
      setCookingRequests(prev => prev.filter(req => req.id !== requestId));

    } catch (e) {
      console.error("Transaction failed: ", e.message);
      setError(`Approval failed: ${e.message}`);
    }
  };

  const handleRejectRequest = async (requestId) => {
    setError(null);

    try {
      // Simply delete the request from the pending collection
      await deleteDoc(doc(db, 'kitchen_requests', requestId));
      console.log("Cooking request rejected successfully!");
      setCookingRequests(prev => prev.filter(req => req.id !== requestId));

    } catch (e) {
      console.error("Rejection failed: ", e.message);
      setError(`Rejection failed: ${e.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'urgent';
      case 'high': return 'high';
      case 'normal': return 'normal';
      case 'low': return 'low';
      default: return 'normal';
    }
  };

  if (loading) {
    return (
      <div className="cooking-request-container">
        <div className="loading">Loading cooking requests...</div>
      </div>
    );
  }

  return (
    <div className="cooking-request-container">
      <h1>Cooking Requests</h1>
      {error && <div className="error-message">{error}</div>}
      <div className="table-wrapper">
        <table className="request-table">
          <thead>
            <tr>
              <th>Meal Time</th>
              <th>Priority</th>
              <th>Date</th>
              <th>Food Items to Cook</th>
              <th>Ingredients Required</th>
              <th>Special Instructions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cookingRequests.length > 0 ? (
              cookingRequests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <span className={`meal-time ${request.mealTime?.toLowerCase()}`}>
                      {request.mealTime || 'Not specified'}
                    </span>
                  </td>
                  <td>
                    <span className={`priority-badge ${getPriorityColor(request.priority)}`}>
                      {request.priority}
                    </span>
                  </td>
                  <td>
                    {formatDate(request.createdAt)}
                  </td>
                  <td>
                    <ul className="food-items-list">
                      {request.foodItems && request.foodItems.map((foodItem, index) => (
                        <li key={index}>
                          <strong>{foodItem.foodItemName}</strong> - {foodItem.totalQuantity} {foodItem.unit}
                          <br />
                          <small>({foodItem.subscriptionCount} subscriptions)</small>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <ul className="ingredients-list">
                      {request.ingredients && request.ingredients.map((ingredient, index) => (
                        <li key={index}>
                          <strong>{ingredient.productName}</strong> - {ingredient.quantity} {ingredient.unit}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <span className="special-instructions">
                      {request.specialInstructions || 'None'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="approve-button" 
                        onClick={() => handleApproveRequest(request.id)}
                      >
                        Approve
                      </button>
                      <button 
                        className="reject-button" 
                        onClick={() => handleRejectRequest(request.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7">No pending cooking requests found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CookingRequest; 